import {
	ForwardedRef,
	forwardRef,
	Fragment,
	FunctionComponent,
	memo,
	useCallback,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';

import Button from '../common/button';
import {
	TDownloadPersonalInfoButtonProps,
	TDownloadPersonalInfoProps,
} from './download-personal-info.type';
import DownloadPersonalInfoView from './download-personal-info.view';

const DownloadPersonalInfoButton = (
	{
		label = 'label',
		buttonProps,
		asChild,
		childrenClassName,
		children,
		onBeforeOpen,
		...props
	}: TDownloadPersonalInfoButtonProps,
	ref: ForwardedRef<{ openModal: () => void }>
) => {
	const [open, setOpen] = useState(false);

	const downloadPersonalInfoModal =
		useRef<FunctionComponent<TDownloadPersonalInfoProps>>();

	const openModal = useCallback(async () => {
		if (!downloadPersonalInfoModal.current) {
			downloadPersonalInfoModal.current = DownloadPersonalInfoView;
		}

		setOpen(true);
	}, [onBeforeOpen, downloadPersonalInfoModal.current]);

	const handleOpenModal = useCallback(async () => {
		const isNext = (await onBeforeOpen?.()) ?? true;
		if (!isNext) {
			return;
		}

		openModal();
	}, [onBeforeOpen, openModal]);

	const onCancel = useCallback(() => {
		setOpen(false);
	}, []);

	useImperativeHandle(
		ref,
		() => ({
			openModal,
		}),
		[openModal]
	);

	return (
		<Fragment>
			{asChild ? (
				<div onClick={handleOpenModal} className={childrenClassName}>
					{children}
				</div>
			) : (
				<Button {...buttonProps} onClick={handleOpenModal}>
					{label}
				</Button>
			)}
			{downloadPersonalInfoModal.current && (
				<downloadPersonalInfoModal.current {...{ ...props, open, onCancel }} />
			)}
		</Fragment>
	);
};

DownloadPersonalInfoButton.displayName = 'DownloadPersonalInfoButton';

export default memo(forwardRef(DownloadPersonalInfoButton));
